import React, { useEffect, useState } from "react";
import { Dropdown, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { emojis } from "../../bootstrap/alertData";
import {
  createRoute,
  DeleteRoute,
  getAllRoutes,
  UpdateRoute,
} from "../../../../APIS";
import { FaArrowLeft, FaEye } from "react-icons/fa";
import Timings from "./Timings";
import Prices from "./Prices";

const ServiceCategory = ({ serviceId = 1 }) => {
  const [routes, setRoutes] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // ✅ Alerts
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" });
  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
  };

  // ✅ Which component is open
  const [openPricesId, setOpenPricesId] = useState(null);
  const [openTimingsId, setOpenTimingsId] = useState(null);

  const itemsPerPage = 3;

  // ✅ Fetch Routes
  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const res = await getAllRoutes(serviceId);
      setRoutes(res.data.routes || []);
    } catch (error) {
      console.error("Error fetching routes:", error);
      showAlert("Failed to load routes", "danger");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  // ✅ Search filter
  const filteredRoutes = routes.filter((route) =>
    route.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const paginatedRoutes = filteredRoutes.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // ✅ Delete Route
  const handleDelete = async (id) => {
    try {
      await DeleteRoute(id);
      fetchRoutes();
      showAlert("Route deleted successfully");
    } catch (error) {
      console.error("Error deleting route:", error);
      showAlert("Failed to delete route", "danger");
    }
  };

  // ✅ Toggle Active
  const handleToggleActive = async (route) => {
    try {
      await UpdateRoute(route.id, { ...route, is_active: !route.is_active });
      fetchRoutes();
      showAlert("Route status updated");
    } catch (error) {
      console.error("Error toggling active:", error);
      showAlert("Failed to update status", "danger");
    }
  };

  // ✅ Handle file input separately
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const previewUrl = URL.createObjectURL(selectedFile);
      setPreviewImage(previewUrl);
      setFormData((prev) => ({
        ...prev,
        image: previewUrl,
      }));
    }
  };

  // ✅ Handle other input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ Save Route (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await UpdateRoute(formData.id, formData);
        showAlert("Route updated successfully");
      } else {
        const payload = {
          ...formData,
          service_id: serviceId,
          cityId: 1,
        };
        await createRoute(payload);
        showAlert("Route added successfully");
      }
      fetchRoutes();
      setFormData({});
      setIsEditMode(false);
      setShowForm(false);
      setPreviewImage(null);
    } catch (error) {
      console.error("Error saving route:", error);
      showAlert("Failed to save route", "danger");
    }
  };

  return (
    <div className="col-12">
      {/* ✅ Alerts */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() => setAlert({ show: false, message: "", variant: "" })}
          className="mb-3"
        >
          {alert.variant === "success" && emojis.success}
          {alert.variant === "danger" && emojis.error} {alert.message}
        </Alert>
      )}

      <div className="card">
        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0 d-flex align-items-center">
            {(openPricesId !== null || openTimingsId !== null) && (
              <FaArrowLeft
                size={18}
                style={{ cursor: "pointer", marginRight: "8px" }}
                onClick={() => {
                  setOpenPricesId(null);
                  setOpenTimingsId(null);
                }}
              />
            )}
            Routes
          </h4>
          {/* Hide Add/Search if pricing or timings open */}
          {openPricesId === null && openTimingsId === null && !showForm && (
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <button
                className="btn btn-secondary"
                style={{ whiteSpace: "nowrap" }}
                onClick={() => {
                  setShowForm(true);
                  setIsEditMode(false);
                  setFormData({});
                  setPreviewImage(null);
                }}
              >
                Add Route
              </button>
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {/* ✅ Show table ONLY if no form, no prices, no timings */}
              {!showForm && openPricesId === null && openTimingsId === null && (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Date From</th>
                        <th>Date To</th>
                        <th>Prices</th>
                        <th>Timings</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRoutes.length > 0 ? (
                        paginatedRoutes.map((route, i) => (
                          <React.Fragment key={route.id}>
                            <tr>
                              <td>{currentPage * itemsPerPage + i + 1}</td>
                              <td>{route.name}</td>
                              <td>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={route.is_active}
                                    style={{ cursor: "pointer" }}
                                    onChange={() => handleToggleActive(route)}
                                  />
                                </div>
                              </td>
                              <td>{route.date_from}</td>
                              <td>{route.date_to}</td>
                              <td className="text-center">
                                <FaEye
                                  className="text-secondary"
                                  size={18}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setOpenPricesId(
                                      openPricesId === route.id ? null : route.id
                                    );
                                    setOpenTimingsId(null);
                                    setShowForm(false);
                                  }}
                                />
                              </td>
                              <td className="text-center">
                                <FaEye
                                  className="text-secondary"
                                  size={18}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setOpenTimingsId(
                                      openTimingsId === route.id ? null : route.id
                                    );
                                    setOpenPricesId(null);
                                    setShowForm(false);
                                  }}
                                />
                              </td>
                              <td>
                                <Dropdown>
                                  <Dropdown.Toggle variant="link">
                                    &#x2022;&#x2022;&#x2022;
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      onClick={() => {
                                        setFormData(route);
                                        setIsEditMode(true);
                                        setShowForm(true);
                                        setPreviewImage(route.image || null);
                                        setOpenPricesId(null);
                                        setOpenTimingsId(null);
                                      }}
                                    >
                                      Edit Route
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      onClick={() => handleDelete(route.id)}
                                    >
                                      Delete Route
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          </React.Fragment>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No routes found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                      Showing{" "}
                      {filteredRoutes.length === 0
                        ? 0
                        : currentPage * itemsPerPage + 1}{" "}
                      to{" "}
                      {Math.min(
                        (currentPage + 1) * itemsPerPage,
                        filteredRoutes.length
                      )}{" "}
                      of {filteredRoutes.length} entries
                    </div>
                    <div>
                      <Link
                        className={`btn btn-sm btn-outline-primary me-2 ${currentPage === 0 ? "disabled" : ""
                          }`}
                        onClick={() =>
                          currentPage > 0 && setCurrentPage(currentPage - 1)
                        }
                      >
                        Prev
                      </Link>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Link
                          key={i}
                          className={`btn btn-sm border ${currentPage === i ? "btn-primary text-white" : ""
                            }`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i + 1}
                        </Link>
                      ))}
                      <Link
                        className={`btn btn-sm btn-outline-primary ms-2 ${currentPage === totalPages - 1 ? "disabled" : ""
                          }`}
                        onClick={() =>
                          currentPage + 1 < totalPages &&
                          setCurrentPage(currentPage + 1)
                        }
                      >
                        Next
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ Prices Component */}
              {openPricesId !== null && (
                <Prices serviceId={openPricesId} />
              )}

              {/* ✅ Timings Component */}
              {openTimingsId !== null && (
                <Timings serviceId={openTimingsId} />
              )}

              {/* ✅ Add/Edit Form */}
              {showForm && (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Name */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">Name</label>
                        <div className="col-lg-7">
                          <input
                            type="text"
                            name="name"
                            value={formData.name || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter route name..."
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Image */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">Image</label>
                        <div className="col-lg-7">
                          <input
                            type="file"
                            name="image"
                            required={!isEditMode}
                            accept="image/*"
                            onChange={handleFileChange}
                            className="form-control"
                          />
                          {previewImage && (
                            <img
                              src={previewImage}
                              alt="Preview"
                              className="mt-2"
                              style={{ width: "100px", height: "auto" }}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">
                          Address
                        </label>
                        <div className="col-lg-7">
                          <input
                            type="text"
                            name="address"
                            value={formData.address || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter address..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">Phone</label>
                        <div className="col-lg-7">
                          <input
                            type="text"
                            name="phone"
                            value={formData.phone || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter phone..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Terminal */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">
                          Terminal
                        </label>
                        <div className="col-lg-7">
                          <input
                            type="text"
                            name="terminal"
                            value={formData.terminal || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter terminal..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Days */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">Days</label>
                        <div className="col-lg-7">
                          <input
                            type="text"
                            name="days"
                            value={formData.days || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter days (comma separated)..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Schedule */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">
                          Schedule?
                        </label>
                        <div className="col-lg-7">
                          <input
                            type="checkbox"
                            name="is_schedule"
                            checked={formData.is_schedule || false}
                            onChange={handleChange}
                            className="form-check-input mt-2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Date From */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">
                          Date From
                        </label>
                        <div className="col-lg-7">
                          <input
                            type="date"
                            name="date_from"
                            value={formData.date_from || ""}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Date To */}
                    <div className="col-xl-6">
                      <div className="mb-3 row">
                        <label className="col-lg-3 col-form-label">
                          Date To
                        </label>
                        <div className="col-lg-7">
                          <input
                            type="date"
                            name="date_to"
                            value={formData.date_to || ""}
                            onChange={handleChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="mb-3 row">
                      <div className="col-lg-3 ms-auto md:mr-[14px]">
                        <button type="submit" className="btn btn-secondary">
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary ms-2"
                          onClick={() => {
                            setIsEditMode(false);
                            setFormData({});
                            setShowForm(false);
                            setPreviewImage(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCategory;